import React from 'react';
import { Gift, Clock, ArrowRight } from 'lucide-react';

/**
 * Attention-grabbing promo banner shown on the tour detail page.
 * Renders nothing if no banner is available.
 * Props:
 *   banner: { code, discount_type, discount_percent, headline, expires_at, scope }
 *   onApply: () => void  — one-tap apply button handler
 */
const PromoBanner = ({ banner, onApply }) => {
  if (!banner || !banner.code) return null;

  const {
    code,
    discount_type,
    discount_percent,
    headline,
    banner_tag,
    banner_subtext,
    banner_cta,
    expires_at,
  } = banner;

  const isFree = discount_type === 'free' || discount_percent === 100;

  // Tag pill — admin override > auto-derived
  const tag =
    (banner_tag && banner_tag.trim()) ||
    (isFree
      ? 'FREE'
      : discount_type === 'percent'
      ? `${discount_percent}% OFF`
      : 'SPECIAL OFFER');

  // Headline — admin override > auto
  const headlineText =
    (headline && headline.trim()) ||
    (isFree
      ? 'This tour is FREE with code below'
      : `Unlock ${discount_percent}% off with the code below`);

  // Optional secondary line
  const subtextText = (banner_subtext && banner_subtext.trim()) || null;

  // CTA label — admin override > default
  const ctaLabel = (banner_cta && banner_cta.trim()) || 'Apply Code';

  const expiryText = (() => {
    if (!expires_at) return null;
    try {
      const d = new Date(expires_at);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  })();

  return (
    <div
      data-testid="promo-banner"
      className="relative overflow-hidden rounded-2xl border-2 border-amber-400 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 text-white shadow-xl mb-6"
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
      <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />

      <div className="relative flex flex-col sm:flex-row items-stretch gap-4 p-5 sm:p-6">
        {/* Left: icon + tag */}
        <div className="flex items-center gap-3 sm:flex-col sm:items-center sm:justify-center sm:w-24">
          <div className="bg-white/25 backdrop-blur rounded-full p-3 inline-flex items-center justify-center">
            <Gift className="w-7 h-7 text-white" />
          </div>
          <span className="inline-block bg-white text-orange-600 font-black uppercase tracking-wider text-xs px-3 py-1 rounded-full shadow-sm">
            {tag}
          </span>
        </div>

        {/* Middle: headline + code + expiry */}
        <div className="flex-1 min-w-0">
          <p className="font-black text-lg sm:text-xl leading-snug drop-shadow-sm" data-testid="promo-banner-headline">
            {headlineText}
          </p>
          {subtextText && (
            <p
              className="text-white/95 text-sm sm:text-base mt-1 font-semibold drop-shadow-sm"
              data-testid="promo-banner-subtext"
            >
              {subtextText}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-white/90 text-sm">Code:</span>
            <code
              data-testid="promo-banner-code"
              className="inline-block bg-white text-orange-600 font-mono font-black text-lg px-3 py-1 rounded-lg tracking-wider shadow-sm"
            >
              {code}
            </code>
            {expiryText && (
              <span className="inline-flex items-center gap-1 text-xs bg-white/20 backdrop-blur px-2 py-1 rounded-full">
                <Clock className="w-3 h-3" />
                Expires {expiryText}
              </span>
            )}
          </div>
        </div>

        {/* Right: CTA */}
        <div className="flex sm:items-center">
          <button
            type="button"
            onClick={onApply}
            data-testid="promo-banner-apply-btn"
            className="w-full sm:w-auto bg-white text-orange-600 hover:bg-orange-50 font-black uppercase tracking-wide text-sm px-5 py-3 rounded-full shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2"
          >
            {ctaLabel}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
